import { config } from '@iheartjane/config';
import type { CardCarouselProps } from '@iheartjane/menu';
import {
  CardCarousel,
  formatFeaturedRow,
  useIsModalOpen,
} from '@iheartjane/menu';
import { useMobileMediaQuery } from '@iheartjane/reefer';
import type { AppMode } from '@iheartjane/types';
import { sentenceCase } from '@iheartjane/utils';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import {
  useIsStoreRefreshEnabled,
  useMenuTopRowNoRefresh,
  useMenuTopRowWithRefresh,
} from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import { buildSearchFilter, formatFeaturedCategory } from '@jane/search/util';
import { MenuProductCarousel } from '@jane/shared-ecomm/components';
import { useMenu } from '@jane/shared-ecomm/providers';
// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';
import type { MenuCategoryPath } from '@jane/shared/types';
import { insertIfArr } from '@jane/shared/util';

import { useComposeMenuFilters } from '../../hooks/useComposeMenuFilters';
import { MenuProductCard } from '../MenuProductCards/MenuProductCard/MenuProductCard';

enum Surface {
  Ecomm = 'ecomm',
  Kiosk = 'kiosk',
  Marketplace = 'marketplace',
}

const getSurfaceByAppMode = (appMode: AppMode): Surface => {
  if (appMode === 'default') {
    return Surface.Marketplace;
  } else if (appMode === 'kiosk') {
    return Surface.Kiosk;
  }
  return Surface.Ecomm;
};

export interface MenuCarouselProps {
  menuCategory?: MenuCategoryPath;
  mode?: CardCarouselProps['mode'];
  placementIndex?: number;
}

const DEFAULT_REFRESH_TIME_IN_SECONDS = 15;

export const useRefreshTime = (store: Store, appMode: AppMode) => {
  const surface = getSurfaceByAppMode(appMode);
  const refreshTimeInMs = store.menu_top_row_refresh_time?.[surface];

  return refreshTimeInMs
    ? refreshTimeInMs / 1000
    : DEFAULT_REFRESH_TIME_IN_SECONDS;
};

export const MenuSponsoredCarousel = ({
  menuCategory,
  mode = 'ideal-width',
}: MenuCarouselProps) => {
  const cardRef = useRef<{ resetScroll: () => void }>(null);
  const isMobile = useMobileMediaQuery({});
  const { featured_category: featuredCategory, rowId: rowIdParam } = useParams<{
    featured_category: string;
    rowId: string;
  }>();

  const { searchState } = useSearchContext();
  const {
    appInfo: { appMode },
    listView,
    paths,
    store,
  } = useMenu();

  const isFeaturedPage = menuCategory === 'featured';
  const isFilteringProducts =
    Object.values(searchState).filter(Boolean).length > 0;
  const filterByCategory =
    menuCategory && !['all', 'featured'].includes(menuCategory);

  const hasFeaturedRow = !!(featuredCategory || rowIdParam);

  const { featuredTitle } = rowIdParam
    ? formatFeaturedRow(
        Number(store.id),
        store.custom_row_settings,
        Number(rowIdParam)
      )
    : formatFeaturedCategory(store, featuredCategory);

  const featuredRowUrl = rowIdParam
    ? paths.menuRow({ id: Number(rowIdParam), title: featuredTitle })
    : paths.menuFeaturedCategory(featuredCategory || '');

  const nextBreadcrumbs = [
    ...insertIfArr(isFeaturedPage, {
      title: 'Featured',
      url: paths.menuCategory('featured'),
    }),
    ...insertIfArr(!isFeaturedPage, {
      title:
        menuCategory === 'all'
          ? 'All products'
          : sentenceCase(menuCategory || ''),
      url: paths.menuCategory(menuCategory as MenuCategoryPath),
    }),
    ...insertIfArr(hasFeaturedRow, {
      title: featuredTitle,
      url: featuredRowUrl,
    }),
  ];

  const isModalOpen = useIsModalOpen();

  const staticFilters = useComposeMenuFilters(
    filterByCategory
      ? [`kind:"${menuCategory}" OR root_types:"${menuCategory}"`]
      : []
  );

  const filteredSearchState = {
    ...searchState,
    filters: { ...searchState?.filters, brand: [] },
  };

  const searchFilter = buildSearchFilter({
    searchState: filteredSearchState,
    staticFilters,
  });

  const refreshTime = useRefreshTime(store, appMode);

  const isStoreRefreshEnabled = useIsStoreRefreshEnabled();

  const {
    isError: isErrorOld,
    isFetching: isFetchingOld,
    pauseRefetching,
    rowInstance: rowInstanceOld,
    unpauseRefetching,
  } = useMenuTopRowWithRefresh({
    enableRefetch: !isModalOpen && !searchState?.searchText,
    enabled: isStoreRefreshEnabled,
    pageCategory: menuCategory,
    refreshTime,
    searchFilter,
    storeId: Number(store.id),
  });

  const {
    isError: isErrorNew,
    isLoading: isFetchingNew,
    instance: rowInstanceNew,
  } = useMenuTopRowNoRefresh({
    enabled: !isStoreRefreshEnabled,
    searchFilter,
    storeId: Number(store.id),
    pageCategory: menuCategory,
    searchAttributes: ['*'],
  });

  const rowInstance = isStoreRefreshEnabled ? rowInstanceOld : rowInstanceNew;
  const isFetching = isStoreRefreshEnabled ? isFetchingOld : isFetchingNew;
  const isError = isStoreRefreshEnabled ? isErrorOld : isErrorNew;

  const rowName = rowInstance?.title ?? '';
  const isInline = !isFeaturedPage || isFilteringProducts || hasFeaturedRow;

  const hideSponsoredCarousel =
    isError || !rowInstance?.products.length || searchState?.searchText;

  useEffect(() => {
    if (!hideSponsoredCarousel) {
      if (cardRef.current) {
        cardRef.current.resetScroll();
      }
    }
  }, [hideSponsoredCarousel]);

  /**
   * This hook is used to trigger the impression event for the products in the sponsored carousel
   * when the carousel is rendered.
   */
  useEffect(() => {
    if (rowInstance) {
      rowInstance.products.forEach((product) => {
        product.impress();
      });
    }
  }, [rowInstance]);

  if (hideSponsoredCarousel) return null;

  return (
    <MenuProductCarousel
      cardRef={cardRef}
      mode={mode}
      listView={listView}
      name={rowName}
      onHover={pauseRefetching}
      onLeave={unpauseRefetching}
      isLoading={isFetching}
      variant={isInline ? 'sponsored-inline' : 'sponsored'}
      mx={isFeaturedPage && !hasFeaturedRow ? 0 : isMobile ? 24 : 64}
      mb={isFeaturedPage && !hasFeaturedRow ? 0 : 24}
    >
      {!isFetching &&
        rowInstance.products.map((product, index) => (
          <CardCarousel.Card
            key={`row-ad-${product.objectId}`}
            width="100%"
            widthMobile="100%"
          >
            <MenuProductCard
              algoliaIndexName={`menu-products-${config.algoliaEnv}`}
              appliedWeightFilter=""
              breadcrumbs={nextBreadcrumbs}
              carouselView={true}
              columnPosition={index}
              menuRowName={rowName}
              productLocation={`${featuredTitle || menuCategory}/${rowName}`}
              product={product}
              rowPosition={0}
              isMenuTopRow={true}
            />
          </CardCarousel.Card>
        ))}
    </MenuProductCarousel>
  );
};
